import { createReducer } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import purgeStoredState from 'redux-persist/es/purgeStoredState';
import storage from 'redux-persist/lib/storage';
import { isUndefined } from 'lodash';
import { getCookie } from '@/utils';
import t from '@/store/types';

// Read stored data in cookies and merge it with the initial state.
interface AuthenticationState {
  token: string | null;
  organizationId: string | null;
  tenantId: string | null;
  userId: string | null;
  locale: string;
  verified: boolean;
  verifyEmail: string | null;
  errors: any[];
}

const initialState: AuthenticationState = {
  token: getCookie('token') || null,
  organizationId: getCookie('organization_id') || null,
  tenantId: getCookie('tenant_id') || null,
  userId: getCookie('authenticated_user_id') || null,
  locale: getCookie('locale') || 'fr', // Default to French (matches organization setup default)
  verified: true, // Let's be optimistic and assume the user's email is confirmed.
  verifyEmail: null,
  errors: [],
};

const STORAGE_KEY = 'leyona:authentication';
const CONFIG = {
  key: STORAGE_KEY,
  whitelist: [],
  storage,
};

const reducerInstance = createReducer(initialState, (builder) => {
  builder
    .addCase(
      t.LOGIN_FAILURE,
      (state, action: { type: string; errors: any }) => {
        state.errors = action.errors;
      },
    )
    .addCase(t.LOGIN_CLEAR_ERRORS, (state) => {
      state.errors = [];
    })
    .addCase(
      t.SET_EMAIL_VERIFIED,
      (
        state,
        action: {
          type: string;
          action: { verified?: boolean; email?: string };
        },
      ) => {
        state.verified = !isUndefined(action.action.verified)
          ? action.action.verified
          : true;
        state.verifyEmail = action.action.email || null;

        if (state.verified) {
          state.verifyEmail = null;
        }
      },
    )
    .addCase(
      t.SET_AUTH_TOKEN,
      (state, action: { type: string; action: { token: string } }) => {
        state.token = action.action.token;
      },
    )
    .addCase(
      t.SET_ORGANIZATIOIN_ID,
      (state, action: { type: string; action: { organizationId: string } }) => {
        state.organizationId = action.action.organizationId;
      },
    )
    .addCase(
      t.SET_TENANT_ID,
      (state, action: { type: string; action: { tenantId: string } }) => {
        state.tenantId = action.action.tenantId;
      },
    )
    .addCase(
      t.SET_USER_ID,
      (state, action: { type: string; action: { userId: string } }) => {
        state.userId = action.action.userId;
      },
    )
    .addCase(t.RESET, () => {
      purgeStoredState(CONFIG);
    });
});

export default persistReducer(CONFIG, reducerInstance);

export const isAuthenticated = (state: any) => !!state.authentication.token;
export const hasErrorType = (state: any, errorType: any) => {
  return state.authentication.errors.find((e: any) => e.type === errorType);
};

export const isTenantSeeded = (state: any) => !!state.tenant.seeded_at;
export const isTenantBuilt = (state: any) => !!state.tenant.initialized_at;

export const isTenantHasSubscription = () => false;
export const isTenantSubscriptionExpired = () => false;
